export default function BirthdayPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="relative h-96 bg-gradient-to-br from-pink-500 to-orange-500 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?w=1200&h=600&fit=crop"
          alt="Birthday celebration"
          className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-40"
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center">
          <h1 className="text-5xl font-bold text-white mb-4">Birthday Gift Exchange</h1>
          <p className="text-xl text-white max-w-2xl">
            Make milestone birthdays memorable with a coordinated group gift exchange
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Celebrate Birthdays Together</h2>
          <p className="text-lg text-gray-600 mb-6">
            Birthday gift exchanges add an element of surprise and fun to celebrations. Whether it's a milestone birthday,
            a surprise party, or an annual tradition, SwapGifts helps coordinate thoughtful giving among friends and family.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="border border-gray-200 rounded-lg p-5">
              <h3 className="font-semibold text-gray-900 mb-2">Ideal occasions</h3>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li>• Milestone birthdays (30th, 40th, 50th)</li>
                <li>• Surprise birthday parties</li>
                <li>• Friend group celebrations</li>
                <li>• Kids' birthday parties with peers</li>
                <li>• Annual birthday clubs</li>
              </ul>
            </div>

            <div className="border border-gray-200 rounded-lg p-5">
              <h3 className="font-semibold text-gray-900 mb-2">Typical budget range</h3>
              <p className="text-gray-600 text-sm mb-3">
                Birthday exchanges vary widely from $20 for casual celebrations to $100+ for milestone events.
                The birthday person might set a theme or price point.
              </p>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mb-4">How to Organize a Birthday Exchange</h3>
          <div className="space-y-4 mb-8">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-pink-500 text-white rounded-full flex items-center justify-center font-bold mr-4">
                1
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Choose your format</h4>
                <p className="text-gray-600 text-sm">
                  Decide if everyone brings a gift for the birthday person, or if it's a group exchange where everyone
                  draws names. For milestone birthdays, consider pooling for one big gift plus smaller surprises.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-pink-500 text-white rounded-full flex items-center justify-center font-bold mr-4">
                2
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Set the guest list</h4>
                <p className="text-gray-600 text-sm">
                  Include close friends, family members, or party attendees. If it's a surprise party, coordinate secretly
                  with a co-organizer who can share the link without spoiling the surprise.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-pink-500 text-white rounded-full flex items-center justify-center font-bold mr-4">
                3
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Consider a theme</h4>
                <p className="text-gray-600 text-sm">
                  Birthday exchanges work great with themes: "experiences not things," "handmade gifts," "memory books,"
                  or interests aligned with the birthday person's hobbies.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-pink-500 text-white rounded-full flex items-center justify-center font-bold mr-4">
                4
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Time the reveal perfectly</h4>
                <p className="text-gray-600 text-sm">
                  Coordinate the gift exchange during the party when energy is high. Consider a dedicated segment where
                  each person explains why they chose their gift for added sentimental value.
                </p>
              </div>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mb-4">Birthday Exchange Ideas</h3>
          <div className="bg-pink-50 border border-pink-200 rounded-lg p-6 mb-8">
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-pink-600 mr-2">•</span>
                <span><strong>Memory exchange:</strong> Everyone brings a framed photo or memento representing a shared memory</span>
              </li>
              <li className="flex items-start">
                <span className="text-pink-600 mr-2">•</span>
                <span><strong>Experience gifts:</strong> Concert tickets, spa days, or adventure vouchers instead of physical items</span>
              </li>
              <li className="flex items-start">
                <span className="text-pink-600 mr-2">•</span>
                <span><strong>Group contribution:</strong> Everyone chips in for one major gift the birthday person truly wants</span>
              </li>
              <li className="flex items-start">
                <span className="text-pink-600 mr-2">•</span>
                <span><strong>Birthday month club:</strong> Set up a recurring exchange where each month someone new is celebrated</span>
              </li>
              <li className="flex items-start">
                <span className="text-pink-600 mr-2">•</span>
                <span><strong>Charitable giving:</strong> Donate to causes the birthday person cares about in their name</span>
              </li>
            </ul>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mb-4">Tips for Meaningful Birthday Gifts</h3>
          <div className="border-l-4 border-pink-500 pl-6 py-4 mb-8">
            <ul className="space-y-2 text-gray-600">
              <li>• Ask the birthday person for a wishlist to ensure gifts are appreciated</li>
              <li>• Consider their current life stage and needs (new parent, recent graduate, retiree)</li>
              <li>• Personalized items show extra thought and effort</li>
              <li>• Include a heartfelt card or note explaining your gift choice</li>
              <li>• For milestone birthdays, coordinate themes across all gifts</li>
            </ul>
          </div>

          <div className="text-center">
            <a
              href="/create"
              className="inline-block px-8 py-4 bg-pink-500 text-white text-lg font-semibold rounded-lg hover:bg-pink-600 transition-colors"
            >
              Start Your Birthday Exchange
            </a>
            <p className="text-sm text-gray-500 mt-3">No registration required • Free to use</p>
          </div>
        </div>

        <div className="bg-gray-100 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-3">Related Resources</h3>
          <div className="flex flex-wrap gap-3">
            <a href="/events/wedding" className="text-sm text-gray-700 hover:text-pink-600">
              Wedding Exchanges →
            </a>
            <a href="/blog" className="text-sm text-gray-700 hover:text-pink-600">
              Gift Ideas Blog →
            </a>
            <a href="/how-it-works" className="text-sm text-gray-700 hover:text-pink-600">
              How It Works →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
